import type { SiteLayer } from '../../types/site';
import { preserveLandGateReview } from './landGatePromotion';

function simpleHash(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i += 1) {
    h = ((h << 5) + h) ^ input.charCodeAt(i);
  }
  return (h >>> 0).toString(16).padStart(8, '0').slice(0, 8);
}

function numericSuffix(id: string): number {
  const m = id.match(/(\d+)(?!.*\d)/);
  if (!m) return -1;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : -1;
}

function layerFreshness(layer: SiteLayer): number {
  const ts = layer.updatedAt ?? layer.createdAt ?? layer.importedAt;
  const parsed = ts ? Date.parse(ts) : NaN;
  return Number.isFinite(parsed) ? parsed : -1;
}

export function layerDedupKey(layer: SiteLayer): string {
  // A CAD entity's deterministic id contains its source drawing hash and
  // entity handle. Distinct handles can legitimately share identical geometry,
  // so the legacy geometry key must never collapse them during editor save or
  // site-load reconciliation. Repeated rows for the same id still deduplicate.
  if (layer.source === 'cad-import') return `cad-import|${layer.id}`;

  const coords = layer.geometry?.coordinates;
  const geomSig = coords
    ? simpleHash(JSON.stringify(coords).slice(0, 200))
    : 'no-geom';
  return `${(layer.name ?? '').trim().toLowerCase()}|${layer.category ?? ''}|${layer.geometry?.type ?? ''}|${geomSig}`;
}

export interface DedupSiteLayersResult {
  layers: SiteLayer[];
  dropped: SiteLayer[];
}

export function dedupSiteLayers(params: {
  siteId: string;
  layers: SiteLayer[] | undefined;
}): DedupSiteLayersResult {
  const input = params.layers ?? [];
  if (input.length < 2) {
    return { layers: input, dropped: [] };
  }

  const grouped = new Map<string, SiteLayer[]>();
  for (const layer of input) {
    const key = layerDedupKey(layer);
    const list = grouped.get(key);
    if (list) list.push(layer);
    else grouped.set(key, [layer]);
  }

  const kept: SiteLayer[] = [];
  const dropped: SiteLayer[] = [];
  for (const [key, group] of grouped) {
    if (group.length === 1) {
      kept.push(group[0]);
      continue;
    }
    const sorted = [...group].sort((a, b) => {
      const byTs = layerFreshness(b) - layerFreshness(a);
      if (byTs !== 0) return byTs;
      const bySuffix = numericSuffix(b.id) - numericSuffix(a.id);
      if (bySuffix !== 0) return bySuffix;
      return b.id.localeCompare(a.id);
    });
    kept.push(preserveLandGateReview(sorted[0], sorted));
    dropped.push(...sorted.slice(1));
    console.warn(`[siteLayers.dedup] site=${params.siteId} key=${key} count=${group.length}`);
  }

  if (dropped.length === 0) {
    return { layers: input, dropped: [] };
  }

  return { layers: kept, dropped };
}
